import { useState } from 'react';
import axios from 'axios';
import { Banner, BlockStack, Button, Card, InlineStack, Text } from '@shopify/polaris';
import { useToast } from '#client/components/common/Toast/ToastContext';
import DatePicker from '#client/pages/Programs/Program/components/ProgramInfo/DatePicker';

function dateInputValue(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export default function InventoryAdjustmentsReport() {
    const { addToast } = useToast();
    const [startDate, setStartDate] = useState(() => {
        const date = new Date();
        date.setDate(1);
        date.setHours(0, 0, 0, 0);
        return date;
    });
    const [endDate, setEndDate] = useState(() => new Date(new Date().setHours(0, 0, 0, 0)));
    const [startDateValid, setStartDateValid] = useState(true);
    const [endDateValid, setEndDateValid] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState('');
    const reversedRange = dateInputValue(startDate) > dateInputValue(endDate);
    const invalidRange = !startDateValid || !endDateValid || reversedRange || !startDate || !endDate;

    const generateReport = async () => {
        if (invalidRange) return;
        setError('');
        setIsGenerating(true);

        try {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(0, 0, 0, 0);
            // Include the entire last day in the user's timezone, including DST changes.
            end.setDate(end.getDate() + 1);
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/app/reports/inventory-adjustments`, {
                params: {
                    startDate: start.toISOString(),
                    endDate: end.toISOString(),
                    timeZone: globalThis.Intl.DateTimeFormat().resolvedOptions().timeZone,
                },
                responseType: 'blob',
            });

            if (response.status === 204) {
                addToast({ type: 'info', message: 'No inventory adjustments found for the specified date range.' });
                return;
            }

            const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = `inventory-adjustments-${dateInputValue(startDate)}-to-${dateInputValue(endDate)}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
            addToast({ type: 'success', message: 'Inventory adjustments report generated.' });
        } catch (reportError) {
            console.error('❌ Error generating report:', reportError);
            const message = 'Unable to generate the report. Please try again.';
            setError(message);
            addToast({ type: 'error', message });
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <Card>
            <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                    Inventory Adjustments Report
                </Text>
                <Text as="p">
                    Download completed inventory adjustments for the location selected. All Locations includes every location you have access to. The date range applies to the submitted date.
                </Text>
                {error && <Banner tone="critical">{error}</Banner>}
                <InlineStack gap="400" wrap>
                    <DatePicker
                        label="Start date"
                        value={startDate}
                        onChange={setStartDate}
                        onValidityChange={setStartDateValid}
                        disabled={isGenerating}
                        error={!startDateValid ? 'Enter a valid start date.' : undefined}
                    />
                    <DatePicker
                        label="End date"
                        value={endDate}
                        onChange={setEndDate}
                        onValidityChange={setEndDateValid}
                        disabled={isGenerating}
                        disableDatesBefore={startDate}
                        error={!endDateValid ? 'Enter a valid end date.' : startDateValid && reversedRange ? 'End date must be on or after start date.' : undefined}
                    />
                </InlineStack>
                <InlineStack>
                    <Button variant="primary" onClick={generateReport} loading={isGenerating} disabled={invalidRange}>
                        Generate Report
                    </Button>
                </InlineStack>
            </BlockStack>
        </Card>
    );
}
